export default function NaplesTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Naples Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Naples travelers actually move helps us anticipate the passport urgencies that arise here. From snowbird migration patterns to last-minute charter departures, Southwest Florida travel operates on a different rhythm than most U.S. markets.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {/* RSW Airport */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">RSW — Southwest Florida International</h3>
                <p className="text-gray-400 text-xs">Fort Myers, ~25 miles from Naples</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              RSW is the primary commercial airport for Naples travelers. Seasonal flight schedules ramp up dramatically November through April, with direct international service to Toronto, Montreal, and seasonal European charters. Many Naples residents book international trips departing from RSW with tight passport validity requirements — some countries require six months beyond your return date.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check your passport expiration before booking RSW international departures. If it expires within six months of your return, renew it first — or call us for expedited processing.
              </p>
            </div>
          </div>

          {/* Cruise Departures */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-ship-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Cruise Departures from South Florida</h3>
                <p className="text-gray-400 text-xs">Port Everglades &amp; Port Miami</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Naples has one of the highest cruise-participation rates per capita in Florida. Residents regularly drive east to Fort Lauderdale or Miami for Caribbean, Mediterranean repositioning, and transatlantic sailings. Many book cruises 30–60 days out during flash sales — then discover their passport is expired or lacks blank pages for visa stamps.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Closed-loop Caribbean cruises only require a birth certificate, but any international itinerary — or a flight connection — requires a valid passport. Verify your cruise line&apos;s policy before sailing.
              </p>
            </div>
          </div>

          {/* Seasonal Spikes */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-calendar-event-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Seasonal Travel Spikes</h3>
                <p className="text-gray-400 text-xs">November–April demand surge</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Naples experiences two distinct passport demand spikes: November through January, when snowbirds arrive and prepare winter European or South American trips, and March through April, when seasonal residents finalize spring Mediterranean or Alaskan itineraries. Government processing slows during these same months due to volume, making expedited service even more critical.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Start your renewal before peak season. Government wait times stretch during snowbird months. Our courier processing remains predictable year-round.
              </p>
            </div>
          </div>

          {/* Snowbird Patterns */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-sun-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Snowbird &amp; Dual-Resident Patterns</h3>
                <p className="text-gray-400 text-xs">Second-home travel behaviors</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Many Naples homeowners maintain dual residency or extended-stay visas in Europe, Canada, or Latin America. These travelers often keep their passports stored in safe-deposit boxes at their northern homes — and only discover expiration issues when unpacking for the season. Last-minute visa renewals, dual-nationality document needs, and emergency replacements are common scenarios here.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Dual residents should keep a photocopy of their passport locally. If the original is at another residence, we can guide you through the replacement process quickly.
              </p>
            </div>
          </div>
        </div>

        {/* Naples Airport / Private Aviation callout */}
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-plane-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Naples Airport (APF) &amp; Private Aviation</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Naples Municipal Airport serves private and charter aviation — a common departure point for affluent residents heading to the Bahamas, Caribbean islands, or European connections. Private aviation passengers still need valid passports for any international destination. We accommodate tight charter departure timelines with processing as fast as 24 hours when needed.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}